import { useSiteData } from "../hooks/use-site-data";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";

interface Site {
  id: string;
  _id?: string;
  siteName?: string;
  updatedAt?: string;
  progress?: number;
  installationType?: string;
  siteType?: string;
  projectId?: string;
  name: string;
  location: string;
  template: string;
  status: "active" | "completed" | "pending";
}

interface AuditSection {
  id: string;
  name: string;
  progress: number;
  status: "completed" | "in-progress" | "pending";
}

interface AuditContextType {
  currentSite?: Site | null;
  selectedSite: Site | null;
  setSelectedSite: (site: Site | null) => void;
  sites: Site[];
  addSite: (site: Site) => void;
  auditSections: AuditSection[];
  updateSectionProgress: (
    sectionId: string,
    progress: number,
    status: string,
  ) => void;
  auditData: Record<string, any>;
  updateAuditData: (sectionId: string, data: any) => void;
  overallProgress: number;
  isAuditComplete: boolean;
}

const AuditContext = createContext<AuditContextType | undefined>(undefined);

const initialMockSites: Site[] = [
  {
    id: "1",
    name: "Solar Farm Alpha",
    location: "Manchester, UK",
    template: "Commercial Solar",
    status: "completed",
  },
  {
    id: "2",
    name: "Rooftop Installation Beta",
    location: "London, UK",
    template: "Residential Solar",
    status: "active",
  },
  {
    id: "3",
    name: "Commercial Site Gamma",
    location: "Birmingham, UK",
    template: "Commercial Solar",
    status: "pending",
  },
  {
    id: "4",
    name: "Industrial Complex Delta",
    location: "Leeds, UK",
    template: "Industrial Solar",
    status: "active",
  },
];

const getInitialSections = (siteId: string): AuditSection[] => {
  // Site 1 (Solar Farm Alpha) - 100% completed for testing
  if (siteId === "1") {
    return [
      {
        id: "project-data",
        name: "Project Data",
        progress: 100,
        status: "completed",
      },
      {
        id: "site-data",
        name: "Site Data",
        progress: 100,
        status: "completed",
      },
      {
        id: "completed-works",
        name: "Completed Works",
        progress: 100,
        status: "completed",
      },
      {
        id: "observations",
        name: "Site Observations",
        progress: 100,
        status: "completed",
      },
      {
        id: "punch-list",
        name: "Punch List",
        progress: 100,
        status: "completed",
      },
      {
        id: "photos",
        name: "Photos & Files",
        progress: 100,
        status: "completed",
      },
      {
        id: "signatures",
        name: "Signatures",
        progress: 100,
        status: "completed",
      },
    ];
  }

  // Other sites with partial completion
  return [
    {
      id: "project-data",
      name: "Project Data",
      progress: 100,
      status: "completed",
    },
    { id: "site-data", name: "Site Data", progress: 80, status: "in-progress" },
    {
      id: "completed-works",
      name: "Completed Works",
      progress: 60,
      status: "in-progress",
    },
    {
      id: "observations",
      name: "Site Observations",
      progress: 30,
      status: "pending",
    },
    { id: "punch-list", name: "Punch List", progress: 0, status: "pending" },
    {
      id: "photos",
      name: "Photos & Files",
      progress: 45,
      status: "in-progress",
    },
    { id: "signatures", name: "Signatures", progress: 0, status: "pending" },
  ];
};

export function SiteProvider({ children }: { children: ReactNode }) {
  const [selectedSite, setSelectedSiteState] = useState<Site | null>(null);
  const [sites] = useState<Site[]>(initialMockSites);
  const [auditSections, setAuditSections] = useState<AuditSection[]>([]);
  const [auditData, setAuditData] = useState<Record<string, any>>({});

  // Load persisted state on mount
  useEffect(() => {
    const savedSiteId = localStorage.getItem("selectedSiteId");
    if (savedSiteId) {
      const site = sites.find((s) => s.id === savedSiteId);
      if (site) {
        setSelectedSiteState(site);
        setAuditSections(getInitialSections(savedSiteId));

        // Load audit data
        const savedData = localStorage.getItem(`audit_${savedSiteId}`);
        if (savedData) {
          try {
            setAuditData(JSON.parse(savedData));
          } catch (error) {
            console.error("Failed to load audit data:", error);
          }
        }
      }
    }
  }, [sites]);

  const setSelectedSite = (site: Site | null) => {
    setSelectedSiteState(site);
    if (site) {
      localStorage.setItem("selectedSiteId", site.id);
      setAuditSections(getInitialSections(site.id));

      // Load existing audit data for this site
      const savedData = localStorage.getItem(`audit_${site.id}`);
      if (savedData) {
        try {
          setAuditData(JSON.parse(savedData));
        } catch (error) {
          console.error("Failed to load audit data:", error);
          setAuditData({});
        }
      } else {
        setAuditData({});
      }
    } else {
      localStorage.removeItem("selectedSiteId");
      setAuditSections([]);
      setAuditData({});
    }
  };

  const updateSectionProgress = (
    sectionId: string,
    progress: number,
    status: string,
  ) => {
    setAuditSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              progress,
              status: status as "completed" | "in-progress" | "pending",
            }
          : section,
      ),
    );
  };

  const updateAuditData = (sectionId: string, data: any) => {
    const newAuditData = {
      ...auditData,
      [sectionId]: data,
    };
    setAuditData(newAuditData);

    // Persist to localStorage
    if (selectedSite) {
      localStorage.setItem(
        `audit_${selectedSite.id}`,
        JSON.stringify(newAuditData),
      );
    }
  };

  const overallProgress =
    auditSections.length > 0
      ? auditSections.reduce((acc, section) => acc + section.progress, 0) /
        auditSections.length
      : 0;

  const isAuditComplete = overallProgress === 100;

  const { siteData, currentSiteData, create } = useSiteData();

  return (
    <AuditContext.Provider
      value={{
        selectedSite,
        setSelectedSite,
        sites: siteData,
        currentSite: currentSiteData,
        // :siteData?.data?.siteData,
        // sites,
        addSite: create,
        auditSections,
        updateSectionProgress,
        auditData,
        updateAuditData,
        overallProgress,
        isAuditComplete,
      }}
    >
      {children}
    </AuditContext.Provider>
  );
}

export function useSite() {
  const context = useContext(AuditContext);
  if (context === undefined) {
    throw new Error("useAudit must be used within an AuditProvider");
  }

  return context;
}

export const mockSites = initialMockSites;
